<template>
  <div class="add-to-cart-content">
    <div class="add-to-cart-content__header">
      <div
        v-for="(item, key) in items"
        :key="key"
      >
        <SvgIcon
          :image="successIcon"
          class="add-to-cart-content__icon"
        />
        <strong><FootnoteText :text="item.name" /></strong>
        wurde in den Warenkorb gelegt
      </div>
    </div>

    <div class="add-to-cart-content__buttons">
      <LinkButton
        tag="nuxt-link"
        outline
        to="/shop/warenkorb"
      >
        Warenkorb ansehen
      </LinkButton>
      <LinkButton
        tag="button"
        autofocus
        primary
        @click="close"
      >
        Weiter einkaufen
      </LinkButton>
    </div>

    <template v-if="hasRecommendations">
      <Heading
        tag="h3"
        spaces-top-rem
        spaces-bottom-rem
      >
        Vielleicht interessierst Du Dich auch für…
      </Heading>

      <DraggableScroller
        ref="draggableScrollbar"
        has-pagination
        show-single-active-pagination
      >
        <OfferTileContainer
          v-for="(offer, key) in truncatedRecommendedOffers"
          :key="key"
          class="add-to-cart-content__recommended-offer"
          v-bind="offer"
          :offer="offer"
          tag-title="h2"
        />
      </DraggableScroller>
    </template>
  </div>
</template>

<script setup lang="ts">
import { useRuntimeConfig } from '#imports';

import { LinkButton, Heading, FootnoteText } from '@i22team/td-longtail-vue-components';

import successIcon from '~/assets/images/check.svg';
import { isManualBundle } from '~/lib/build-bundle-from-products';
import type { AddToCartItem } from '~/helpers/add-to-cart';
import type {
  OfferRecommendationsQuery,
  OfferRecommendationsQueryVariables,
} from '#shared/types/goliath/goliath.types';
import DraggableScroller from '~/components/draggable-scroller.vue';
import OfferTileContainer from '~/components/offer-tile-container.vue';

export type AddToCartDialogContentProps = {
  items: AddToCartItem[];
  close: () => void;
};

const props = withDefaults(defineProps<AddToCartDialogContentProps>(), {});

const recommendedOffers = ref<ListOffer[]>([]);

const hasRecommendations = computed(() => recommendedOffers.value.length > 0);
const truncatedRecommendedOffers = computed(() => recommendedOffers.value.slice(0, 4));

const config = useRuntimeConfig();

const fetchOfferRecommendations = async () => {
  const articleNumber = props.items?.[0]?.articleNumber;
  if (!articleNumber || isManualBundle(articleNumber)) return;
  try {
    const { $goliathApi } = useNuxtApp();
    const data = await $goliathApi<
      OfferRecommendationsQueryVariables,
      OfferRecommendationsQuery,
      'offer'
    >(
      'offer-recommendations',
      {
        articleNumber,
      },
      'offer',
      false
    );

    if (data?.recommendations) {
      recommendedOffers.value = data.recommendations as unknown as ListOffer[];
    }
  } catch (error) {
    console.error(error);
  }
};

onMounted(async () => {
  if (config.public.performanceMode.enabled) return;

  await nextTick();

  await fetchOfferRecommendations();
});
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.add-to-cart-content {
  margin-block: 1rem;

  &__recommendations {
    width: 100%;
    margin-top: 2 * $grid-base;
    justify-content: center;
  }

  &__header {
    position: relative;
    text-align: left;
    padding-right: 2.5 * $grid-base;
    padding-left: 2.5 * $grid-base;
    padding-bottom: $grid-base;
    color: var(--color-success);

    @include mq($mq-sm) {
      text-align: center;
    }
  }

  &__icon {
    margin-right: $grid-base;
  }

  &__buttons {
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    grid-gap: $grid-base;
    justify-content: center;
  }

  :deep(.draggable-scroller__container) {
    display: flex;
    gap: 1rem;
    scroll-snap-type: x mandatory;
    max-width: calc(100vw - 3rem);
  }

  &__recommended-offer {
    scroll-snap-align: start;
    height: auto;
    flex: 0 0 calc(100% - 0.5rem);
    text-align: left;

    @include mq($mq-sm) {
      flex: 0 0 calc(50% - 0.5rem);
    }

    @include mq($mq-md) {
      flex: 0 0 calc(33% - 0.5rem);
    }

    @include mq($mq-lg) {
      flex: 0 0 calc(25% - 1rem);
    }
  }
}
</style>
